var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
/* eslint-disable func-names */
import { types, flow, getSnapshot, } from 'mobx-state-tree';
import debounceFlow from '../_mobxStateTree/DebounceFlow';
import createLocalEntityProvider from '../localEntityManagement/LocalEntityProvider';
import { getRootStore } from '../_mobxStateTree/MobxStateTreeHelpers';
import QueryParameters from '../api/base/models/QueryParameters';
import ModelToSnapshotMapper from '../mappers/ModelToSnapshotMapper/ModelToSnapshotMapper';
import SnapshotToStoreMapper from '../mappers/SnapshotToStoreMapper/SnapshotToStoreMapper';
import applyQueryParameters from '../api/base/query/ExpressionBuilder';
import { invokeEventHandler } from '../../helpers/ReactShortcuts';
import QueryView from '../localEntityManagement/QueryView';
/**
 *
 * @param modelType
 * @param apiService
 * @param defaultQueryParameters The default query parameters will be used when you call just query()
 * For all other queries, don't create an action which excepts query parameters, whenever possible!
 */
var createDataAccessStore = function (modelType, apiService, dtoMapper, defaultQueryParameters) {
    var LocalEntityProvider = createLocalEntityProvider(modelType);
    var ModelTypeQueryView = QueryView(modelType);
    return types
        .model({
        localEntityProvider: types.optional(LocalEntityProvider, function () {
            return LocalEntityProvider.create();
        }),
        isLoading: types.optional(types.boolean, false),
        activeItem: types.maybe(types.reference(modelType)),
        selectedItems: types.optional(types.array(types.reference(modelType)), []),
        storedQueries: types.optional(types.map(QueryParameters), {}),
        loadingStoredQueries: types.optional(types.array(types.string), []),
        getDisabled: types.optional(types.boolean, false),
        queryDisabled: types.optional(types.boolean, false),
        queryViews: types.array(ModelTypeQueryView),
    })
        .named(modelType.name + "BaseStore")
        .volatile(function (_) { return ({
        apiServiceQueryMethodsOfStoredQueries: new Map(),
    }); })
        .views(function (self) { return ({
        get items() {
            return self.localEntityProvider.instanceCache.instances;
        },
        get modelType() {
            return modelType;
        },
        get rootStore() {
            return getRootStore(self);
        },
        getItem: function (id) {
            return self.localEntityProvider.get(id);
        },
        getItems: function (ids) {
            return self.localEntityProvider.getMany(ids);
        },
        // ToDo: Validate if this will update the GUI after the instances have changed.
        // ToDo: If not:
        // Create an action setXyQuery()
        // Create an view getter getXyQuery()
        // ToDo: Should work ;)
        /**
         * Use this to bind a filtered list of items to your view
         * Make sure to call the query(queryParameters) action, to get the data back that you want to show
         * This action wil first load the entities from the local database to the InstanceCache
         * and after that sends an API request to get all new/updated entities also in this view here
         */
        queryItems: function (queryParametersOrKey) {
            var queryParameters = typeof queryParametersOrKey === 'string'
                ? self.storedQueries.get(queryParametersOrKey)
                : queryParametersOrKey;
            // return an empty array if the query parameters for the key are not stored
            if (queryParametersOrKey && !queryParameters) {
                console.warn("no query parameters for key " + queryParametersOrKey);
                return [];
            }
            return applyQueryParameters(self.localEntityProvider.instanceCache.instances, queryParameters || defaultQueryParameters);
        },
        // #region stored query
        getStoredQuery: function (queryKey) {
            return self.storedQueries.get(queryKey);
        },
        getLoadMoreTokenOfStoredQuery: function (queryKey) {
            var storedQuery = this.getStoredQuery(queryKey);
            if (!storedQuery) {
                return undefined;
            }
            return "" + storedQuery.getLoadMoreToken(this.queryItems(queryKey));
        },
        storedQueryIsLoading: function (queryKey) {
            return self.loadingStoredQueries.includes(queryKey);
        },
        // #endregion
        //#region query view
        getQueryView: function (queryViewKey) {
            return self.queryViews.find(function (x) { return x.key === queryViewKey; });
        },
        getLoadMoreTokenOfQueryView: function (queryViewKey) {
            var queryView = this.getQueryView(queryViewKey);
            return queryView === null || queryView === void 0 ? void 0 : queryView.loadMoreToken;
        },
        //#endregion
        // #region item selection
        isSelected: function (item) {
            return self.selectedItems.find(function (x) { return x.id === item.id; }) !== undefined;
        },
        areSelected: function (items) {
            var _this = this;
            return items.find(function (x) { return _this.isSelected(x) === false; }) === undefined;
        },
        // #endregion
        // #region navigate activeItem
        getPreviousItem: function (queryKey) {
            if (!self.activeItem) {
                return undefined;
            }
            var dataItems = this.queryItems(queryKey);
            var indexOfActiveDataItem = self.activeItem
                ? dataItems.indexOf(self.activeItem)
                : -1;
            indexOfActiveDataItem--;
            if (indexOfActiveDataItem < 0) {
                return undefined;
            }
            return dataItems[indexOfActiveDataItem];
        },
        getNextItem: function (queryKey) {
            if (!self.activeItem) {
                return undefined;
            }
            var dataItems = this.queryItems(queryKey);
            var lastItemIndex = dataItems.length - 1;
            var indexOfActiveDataItem = self.activeItem
                ? dataItems.indexOf(self.activeItem)
                : lastItemIndex;
            indexOfActiveDataItem++;
            if (indexOfActiveDataItem > lastItemIndex) {
                return undefined;
            }
            return dataItems[indexOfActiveDataItem];
        },
        hasNextItem: function (queryKey) {
            return this.getNextItem(queryKey) !== undefined;
        },
        hasPreviousItem: function (queryKey) {
            return this.getPreviousItem(queryKey) !== undefined;
        },
        // #endregion
    }); })
        .actions(function (self) {
        var modelToSnapshotMapper = new ModelToSnapshotMapper(self.modelType);
        var mapSingle = function (model) {
            var rootStore = getRootStore(self);
            var snapshotToStoreMapper = new SnapshotToStoreMapper(rootStore);
            try {
                var snapshots = modelToSnapshotMapper.mapSingle(model);
                snapshots.forEach(function (snapshotMap) {
                    snapshotMap.forEach(function (value, key) {
                        snapshotToStoreMapper.map(value, key);
                    });
                });
            }
            catch (e) {
                console.error('BaseDataAccessStore.mapSingle failed', e);
            }
        };
        var mapMany = function (models) {
            var rootStore = getRootStore(self);
            var snapshotToStoreMapper = new SnapshotToStoreMapper(rootStore);
            try {
                var snapshotsMaps = modelToSnapshotMapper.mapMany(models);
                var act_1 = undefined;
                try {
                    snapshotsMaps.forEach(function (value, key) {
                        act_1 = value;
                        snapshotToStoreMapper.map(value, key);
                    });
                }
                catch (e) {
                    console.error('BaseDataAccessStore.mapMany failed', e);
                    console.error('Failed at', act_1);
                    console.trace('Trace');
                }
            }
            catch (error) {
                console.error('ERRR', error);
            }
        };
        var setIsLoading = function (isLoading) {
            self.setIsLoading(isLoading);
        };
        return {
            setIsLoading: function (isLoading) {
                self.isLoading = isLoading;
            },
            storedQueryLoadingDone: function (storedQueryKey) {
                self.loadingStoredQueries.remove(storedQueryKey);
            },
            addDto: function (dto) {
                var model = dtoMapper.mapToModel(dto);
                mapSingle(model);
            },
            get: flow(function (id) {
                var _this = this;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0: return [4 /*yield*/, (function () { return __awaiter(_this, void 0, void 0, function () {
                                var apiResult, model, error_1;
                                return __generator(this, function (_a) {
                                    switch (_a.label) {
                                        case 0:
                                            if (self.getDisabled) {
                                                return [2 /*return*/, new Promise(function (resolve) { return resolve(); })];
                                            }
                                            _a.label = 1;
                                        case 1:
                                            _a.trys.push([1, 3, 4, 5]);
                                            setIsLoading(true);
                                            return [4 /*yield*/, apiService.get(id)];
                                        case 2:
                                            apiResult = _a.sent();
                                            if (!apiResult.ok) {
                                                return [2 /*return*/, apiResult];
                                            }
                                            model = dtoMapper.mapToModel(apiResult.data);
                                            return [2 /*return*/, mapSingle(model)];
                                        case 3:
                                            error_1 = _a.sent();
                                            console.error('BaseDataAccessStore.get flow error: ', error_1);
                                            throw new Error('BaseDataAccessStore.get flow error: ');
                                        case 4:
                                            setIsLoading(false);
                                            return [7 /*endfinally*/];
                                        case 5: return [2 /*return*/];
                                    }
                                });
                            }); })()];
                        case 1: return [2 /*return*/, _a.sent()];
                    }
                });
            }),
            /**
             * Just call this when your view gets mounted for example
             * This will not return models, it will only return:
             * void => means everything was ok, nothing to do
             * ApiResult => means some error happened, that apiResult contains more information
             * Bind your view always to the filtered(queryParameters) view
             */
            query: function (queryParametersOrKey, apiServiceQueryMethod, onModelsMapped) {
                var generator = function () {
                    var queryParameters;
                    var _this = this;
                    return __generator(this, function (_a) {
                        switch (_a.label) {
                            case 0:
                                if (self.queryDisabled) {
                                    return [2 /*return*/, new Promise(function (resolve) { return resolve(); })];
                                }
                                queryParameters = typeof queryParametersOrKey === 'string'
                                    ? self.storedQueries.get(queryParametersOrKey)
                                    : queryParametersOrKey;
                                return [4 /*yield*/, (function () { return __awaiter(_this, void 0, void 0, function () {
                                        var apiResult, models, e_1;
                                        return __generator(this, function (_a) {
                                            switch (_a.label) {
                                                case 0:
                                                    _a.trys.push([0, 2, 3, 4]);
                                                    if (typeof queryParametersOrKey === 'string') {
                                                        self.loadingStoredQueries.push(queryParametersOrKey);
                                                    }
                                                    setIsLoading(true);
                                                    // if no query method provided and queryKey is used, try to get the method of the query
                                                    if (!apiServiceQueryMethod) {
                                                        if (typeof queryParametersOrKey === 'string') {
                                                            apiServiceQueryMethod = self.apiServiceQueryMethodsOfStoredQueries.get(queryParametersOrKey);
                                                        }
                                                    }
                                                    if (!apiServiceQueryMethod) {
                                                        apiServiceQueryMethod = apiService.query;
                                                    }
                                                    return [4 /*yield*/, apiServiceQueryMethod(queryParameters || defaultQueryParameters)];
                                                case 1:
                                                    apiResult = _a.sent();
                                                    if (!apiResult.ok) {
                                                        return [2 /*return*/, apiResult];
                                                    }
                                                    models = dtoMapper.mapToModels(apiResult.data);
                                                    if (queryParameters && models.length) {
                                                        queryParameters.setSearchAfterFromItems(models);
                                                    }
                                                    mapMany(models);
                                                    invokeEventHandler(onModelsMapped, models);
                                                    return [3 /*break*/, 4];
                                                case 2:
                                                    e_1 = _a.sent();
                                                    console.error(e_1);
                                                    throw new Error("query error" + JSON.stringify(e_1));
                                                case 3:
                                                    setIsLoading(false);
                                                    if (typeof queryParametersOrKey === 'string') {
                                                        self.storedQueryLoadingDone(queryParametersOrKey);
                                                    }
                                                    return [7 /*endfinally*/];
                                                case 4: return [2 /*return*/];
                                            }
                                        });
                                    }); })()];
                            case 1: return [2 /*return*/, _a.sent()];
                        }
                    });
                };
                if (typeof queryParametersOrKey === 'string') {
                    return flow(generator)();
                }
                return flow(generator)();
            },
            debounceQuery: function (queryParametersOrKey, apiServiceQueryMethod, onExecute) {
                return debounceFlow(typeof queryParametersOrKey === 'string'
                    ? queryParametersOrKey
                    : "query" + modelType.name, function () {
                    return __generator(this, function (_a) {
                        invokeEventHandler(onExecute);
                        self.query(queryParametersOrKey, apiServiceQueryMethod);
                        return [2 /*return*/];
                    });
                })();
            },
            create: flow(function (mstModel, metadata) {
                var dto, apiResult;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            dto = dtoMapper.mapToDto(getSnapshot(mstModel));
                            return [4 /*yield*/, apiService.create(dto, metadata)];
                        case 1:
                            apiResult = (_a.sent());
                            if (apiResult.ok) {
                                self.createItem(mstModel);
                            }
                            return [2 /*return*/, apiResult];
                    }
                });
            }),
            createItem: function (item) {
                self.items.push(item);
            },
            patch: flow(function (id, jsonPatchDocument) {
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0: return [4 /*yield*/, apiService.patch(id, jsonPatchDocument)];
                        case 1: return [2 /*return*/, _a.sent()];
                    }
                });
            }),
            delete: flow(function (id) {
                var selected, apiResult;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            selected = self.selectedItems.find(function (x) { return x.id === id; });
                            if (selected) {
                                self.selectedItems.remove(selected);
                            }
                            return [4 /*yield*/, apiService.delete(id)];
                        case 1:
                            apiResult = (_a.sent());
                            if (apiResult.ok) {
                                self.items.splice(self.items.indexOf(self.items.find(function (x) { return x.id === id; })), 1);
                            }
                            return [2 /*return*/, apiResult];
                    }
                });
            }),
            addOrApplySnapshot: function (snapshot) {
                self.localEntityProvider.addOrApplySnapshot(snapshot);
            },
            // #region disable API actions
            setGetDisabled: function (getDisabled) {
                self.getDisabled = getDisabled;
            },
            setQueryDisabled: function (queryDisabled) {
                self.queryDisabled = queryDisabled;
            },
            // #endregion
            // #region local actions
            deleteItem: function (id) {
                return __awaiter(this, void 0, void 0, function () {
                    return __generator(this, function (_a) {
                        switch (_a.label) {
                            case 0: return [4 /*yield*/, this.deleteItems([id])];
                            case 1:
                                _a.sent();
                                return [2 /*return*/];
                        }
                    });
                });
            },
            deleteItems: function (ids) {
                return __awaiter(this, void 0, void 0, function () {
                    var instancesToRemove;
                    var _this = this;
                    return __generator(this, function (_a) {
                        instancesToRemove = self.getItems(ids);
                        instancesToRemove.forEach(function (instanceToRemove) {
                            var _a;
                            if (((_a = self.activeItem) === null || _a === void 0 ? void 0 : _a.id) === instanceToRemove.id) {
                                _this.unsetActiveItem();
                            }
                            self.queryViews.forEach(function (x) { return x.deleteItems(ids); });
                            _this.deselectItem(instanceToRemove);
                            self.localEntityProvider.delete(instanceToRemove.id);
                        });
                        return [2 /*return*/];
                    });
                });
            },
            deleteAllItems: function () {
                self.queryViews.forEach(function (x) { return x.clearItems(); });
                self.localEntityProvider.deleteAll();
            },
            // #endregion
            // #region stored query
            putStoredQuery: function (queryKey, queryParameters, withoutQuery, apiServiceQueryMethod) {
                if (withoutQuery === void 0) { withoutQuery = false; }
                self.storedQueries.set(queryKey, queryParameters);
                if (apiServiceQueryMethod) {
                    self.apiServiceQueryMethodsOfStoredQueries.set(queryKey, apiServiceQueryMethod);
                }
                else {
                    self.apiServiceQueryMethodsOfStoredQueries.delete(queryKey);
                }
                if (!withoutQuery) {
                    this.query(queryKey);
                }
                return queryParameters;
            },
            getOrAddStoredQuery: function (queryKey, defaultQueryParameters, withoutQuery) {
                if (withoutQuery === void 0) { withoutQuery = false; }
                var storedQuery = self.storedQueries.get(queryKey);
                if (storedQuery) {
                    return storedQuery;
                }
                return this.putStoredQuery(queryKey, defaultQueryParameters, withoutQuery);
            },
            setStoredQueryOrderBy: function (queryKey, orderBy, sortOrder, withoutQuery) {
                var storedQueryParameters = self.getStoredQuery(queryKey);
                if (!storedQueryParameters) {
                    console.error("You try to update the orderBy of the stored query with query key " + queryKey + ", but it does not exists.");
                    return;
                }
                storedQueryParameters.removeSearchAfter();
                storedQueryParameters.putSorting(orderBy, sortOrder);
                if (!withoutQuery) {
                    this.query(queryKey);
                }
            },
            setStoredQuerySortingsSortOrder: function (queryKey, sortOrder, withoutQuery) {
                var storedQueryParameters = self.getStoredQuery(queryKey);
                if (!storedQueryParameters) {
                    console.error("You try to update the orderBy of the stored query with query key " + queryKey + ", but it does not exists.");
                    return;
                }
                storedQueryParameters.setSortOrderOnAllSortings(sortOrder);
                if (!withoutQuery) {
                    this.query(queryKey);
                }
            },
            // #endregion
            //#region query view
            setQueryViewOrderBy: function (queryViewKey, orderBy, sortOrder, withoutQuery) {
                var queryView = self.getQueryView(queryViewKey);
                if (!queryView) {
                    console.error("You try to update the orderBy of the query view with key " + queryViewKey + ", but it does not exists.");
                    return;
                }
                queryView.queryParameters.removeSearchAfter();
                queryView.queryParameters.putSorting(orderBy, sortOrder);
                if (!withoutQuery) {
                    this.queryQueryView(queryViewKey);
                }
            },
            setQueryViewSortingsSortOrder: function (queryViewKey, sortOrder, withoutQuery) {
                var queryView = self.getQueryView(queryViewKey);
                if (!queryView) {
                    console.error("You try to update the orderBy of the query view with key " + queryViewKey + ", but it does not exists.");
                    return;
                }
                queryView.queryParameters.setSortOrderOnAllSortings(sortOrder);
                if (!withoutQuery) {
                    this.queryQueryView(queryViewKey);
                }
            },
            putQueryView: function (queryViewKey, queryParameters, apiServiceQueryMethod) {
                var queryView = self.getQueryView(queryViewKey);
                // create query view if not exists
                if (!queryView) {
                    queryView = ModelTypeQueryView.create({
                        key: queryViewKey,
                        queryParameters: queryParameters,
                    });
                    // ToDo: set load more
                    queryView.setLoadMoreHandler(this.queryQueryView);
                    self.queryViews.push(queryView);
                }
                else {
                    queryView.setQueryParameters(queryParameters);
                }
                if (apiServiceQueryMethod) {
                    self.apiServiceQueryMethodsOfStoredQueries.set(queryViewKey, apiServiceQueryMethod);
                }
                return queryView;
            },
            queryQueryView: function (queryViewKey, queryParameters, apiServiceQueryMethod) {
                if (queryParameters) {
                    this.putQueryView(queryViewKey, queryParameters, apiServiceQueryMethod);
                }
                var queryView = self.getQueryView(queryViewKey);
                if (!queryView) {
                    console.error('queryView is undefined', queryViewKey);
                    return;
                }
                queryView.setLoading(true);
                // update query parameters
                this.query(queryView.queryParameters, self.apiServiceQueryMethodsOfStoredQueries.get(queryViewKey), function (models) {
                    queryView === null || queryView === void 0 ? void 0 : queryView.pushItems(models);
                })
                    .finally(function () {
                    queryView === null || queryView === void 0 ? void 0 : queryView.setLoading(false);
                });
                return queryView;
            },
            debounceQueryQueryView: function (queryViewKey, queryParameters, apiServiceQueryMethod) {
                return debounceFlow(typeof queryViewKey === 'string'
                    ? queryViewKey
                    : "query" + modelType.name, function () {
                    return __generator(this, function (_a) {
                        self.queryQueryView(queryViewKey, queryParameters, apiServiceQueryMethod);
                        return [2 /*return*/];
                    });
                })();
            },
            //#endregion
            // #region activeItem
            setActiveItem: function (item) {
                var _this = this;
                if (typeof item === 'string') {
                    var existingItem = self.getItem(item);
                    if (existingItem) {
                        self.activeItem = existingItem;
                    }
                    else {
                        this.get(item).then(function () {
                            var apiItem = self.getItem(item);
                            if (!apiItem) {
                                return;
                            }
                            _this.setActiveItem(apiItem);
                        });
                    }
                }
                else {
                    self.activeItem = item;
                }
            },
            unsetActiveItem: function () {
                self.activeItem = undefined;
            },
            // #endregion
            // #region navigate activeItem
            goToNextItem: function (queryKey) {
                var nextItem = self.getNextItem(queryKey);
                if (nextItem) {
                    self.activeItem = nextItem;
                }
            },
            goToPreviousItem: function (queryKey) {
                var previousItem = self.getPreviousItem(queryKey);
                if (previousItem) {
                    self.activeItem = previousItem;
                }
            },
            // #endregion
            // #region selected items
            selectItem: function (item) {
                var existing = self.selectedItems.find(function (x) { return x.id === item.id; });
                if (existing) {
                    return;
                }
                self.selectedItems.push(item);
            },
            deselectItem: function (item) {
                var existing = self.selectedItems.find(function (x) { return x.id === item.id; });
                if (!existing) {
                    return;
                }
                self.selectedItems.remove(existing);
            },
            toggleSelectItem: function (item) {
                if (self.isSelected(item)) {
                    this.deselectItem(item);
                }
                else {
                    this.selectItem(item);
                }
            },
            clearSelectedItems: function () {
                self.selectedItems.clear();
            },
            selectItems: function (items) {
                var _this = this;
                items.forEach(function (x) { return _this.selectItem(x); });
            },
            deselectItems: function (items) {
                var _this = this;
                items.forEach(function (x) { return _this.deselectItem(x); });
            },
            // #endregion
        };
    });
};
export default createDataAccessStore;
//# sourceMappingURL=BaseDataAccessStore.js.map